// Pure game logic for Vibecheck Blitz: share text, streaks, local state.

export const ROUNDS = 5;
export const MAX_ROUND_SCORE = 100;
export const MAX_TOTAL = ROUNDS * MAX_ROUND_SCORE;

export interface Pin { x: number; y: number }

export interface Reveal {
  score: number;
  target: { id: string; name: string; artist: string; image: string; valence: number; energy: number };
}

/** One square per round, by how close the pin landed. */
export const roundEmoji = (score: number): string =>
  score >= 80 ? '🟩' : score >= 50 ? '🟨' : score >= 25 ? '🟧' : '🟥';

export const verdict = (total: number): string =>
  total >= 450 ? 'Perfect ears' : total >= 350 ? 'Great vibes' : total >= 250 ? 'Solid instincts' : total >= 150 ? 'Getting warmer' : 'Keep listening';

export interface ShareInput {
  number: number;
  scores: number[];
  betterThan: number | null;
  url: string;
}

export const buildShareText = (s: ShareInput): string => {
  const total = s.scores.reduce((a, b) => a + b, 0);
  const lines = [
    `Vibecheck Blitz #${s.number} 🎧 ${total}/${MAX_TOTAL}`,
    s.scores.map(roundEmoji).join(''),
  ];
  if (s.betterThan !== null) lines.push(`Better than ${s.betterThan}% of players`);
  lines.push(s.url);
  return lines.join('\n');
};

// ---------------------------------------------------------------- storage ---
// Everything here is per-device convenience; failures (private mode etc.) are swallowed.

export interface DayState {
  date: string;
  pins: Pin[];
  reveals: Reveal[];
}

export interface Streak {
  current: number;
  best: number;
  lastPlayed: string | null;
  played: number;
}

export const EMPTY_STREAK: Streak = { current: 0, best: 0, lastPlayed: null, played: 0 };

const safeGet = (k: string): string | null => {
  try { return localStorage.getItem(k); } catch { return null; }
};
const safeSet = (k: string, v: string) => {
  try { localStorage.setItem(k, v); } catch { /* ignore */ }
};

const uuid = (): string =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

export const getPlayerId = (): string => {
  const existing = safeGet('vibecheck:player');
  if (existing && /^[A-Za-z0-9-]{8,64}$/.test(existing)) return existing;
  const id = uuid();
  safeSet('vibecheck:player', id);
  return id;
};

export const loadDayState = (date: string): DayState => {
  try {
    const raw = safeGet('vibecheck:blitz');
    if (raw) {
      const p = JSON.parse(raw) as DayState;
      if (p.date === date && Array.isArray(p.pins) && Array.isArray(p.reveals) && p.pins.length === p.reveals.length) return p;
    }
  } catch { /* fall through */ }
  return { date, pins: [], reveals: [] };
};

export const saveDayState = (s: DayState) => safeSet('vibecheck:blitz', JSON.stringify(s));

export const loadStreak = (): Streak => {
  try {
    const raw = safeGet('vibecheck:streak2');
    if (raw) return { ...EMPTY_STREAK, ...JSON.parse(raw) };
  } catch { /* fall through */ }
  return EMPTY_STREAK;
};

const dayDiff = (a: string, b: string): number =>
  Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86400000);

/** Record a finished day (all five rounds). Idempotent for the same date. */
export const recordPlay = (prev: Streak, date: string): Streak => {
  if (prev.lastPlayed === date) return prev;
  const continues = prev.lastPlayed !== null && dayDiff(prev.lastPlayed, date) === 1;
  const current = continues ? prev.current + 1 : 1;
  return { current, best: Math.max(prev.best, current), lastPlayed: date, played: prev.played + 1 };
};

export const saveStreak = (s: Streak) => safeSet('vibecheck:streak2', JSON.stringify(s));

export const hasSeenIntro = (): boolean => safeGet('vibecheck:intro') === '1';
export const markIntroSeen = () => safeSet('vibecheck:intro', '1');
